import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { pagesAPI, documentsAPI, SITE_NAMES, SITE_COLORS } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { RichTextContent } from '../ui/rich-text-content';
import { ArrowLeft, FileText, Download, FolderOpen } from 'lucide-react';
import { SEO, buildOrganizationJsonLd } from '../seo/SEO';

export const AboutPage = ({ siteKey }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const siteName = SITE_NAMES[siteKey];
  const colors = SITE_COLORS[siteKey];

  useEffect(() => {
    const loadContent = async () => {
      try {
        const response = await pagesAPI.getPage(siteKey, 'about');
        setContent(response.data);
      } catch (error) {
        console.error('Error loading content:', error);
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, [siteKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <SEO
        siteKey={siteKey}
        title="O nás"
        description={`Informace o ${siteName} v Nové Roli. Naše hodnoty, přístup k výchově, prostředí a zázemí pro předškolní děti.`}
        jsonLd={buildOrganizationJsonLd(siteKey)}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <Link to={`/${siteKey}`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] mb-4" data-testid="back-link">
            <ArrowLeft size={18} />
            Zpet na uvodní stránku
          </Link>
          <h1 className="text-4xl font-bold text-[#2D3748]" data-testid="page-title">{content?.title || `O nás - ${siteName}`}</h1>
        </div>

        <div className="rounded-3xl overflow-hidden mb-10">
          <img 
            src={siteKey === 'ms' 
              ? "https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=1200&q=80" 
              : "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=1200&q=80"
            }
            alt={siteName} 
            className="w-full h-64 lg:h-96 object-cover"
          />
        </div>

        <div className="bg-white rounded-3xl border-3 border-[#F0F4F8] p-8 lg:p-12">
          {content?.content ? (
            <RichTextContent html={content.content} />
          ) : (
            <p className="text-[#64748B]">Obsah stránky nebyl nalezen.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export const ParentsPage = ({ siteKey }) => {
  const [content, setContent] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const siteName = SITE_NAMES[siteKey];
  const colors = SITE_COLORS[siteKey];

  useEffect(() => {
    const loadData = async () => {
      try {
        const [pageRes, docsRes] = await Promise.all([
          pagesAPI.getPage(siteKey, 'parents'),
          documentsAPI.getAll(siteKey, 'pro_rodice')
        ]);
        setContent(pageRes.data);
        setDocuments(docsRes.data || []);
      } catch (error) {
        console.error('Error loading content:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [siteKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <SEO
        siteKey={siteKey}
        title="Pro rodiče"
        description={`Sekce pro rodiče v ${SITE_NAMES[siteKey]}. Praktické informace, formuláře ke stažení, kontakty a užitečné odkazy pro rodiče předškolních dětí.`}
        keywords={['pro rodiče', 'informace pro rodiče', 'školka']}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <Link to={`/${siteKey}`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] mb-4" data-testid="back-link">
            <ArrowLeft size={18} />
            Zpet na uvodní stránku
          </Link>
          <h1 className="text-4xl font-bold text-[#2D3748]" data-testid="page-title">{content?.title || 'Pro rodice'}</h1>
          <p className="text-[#64748B] mt-2">Dulezité informace pro rodice našich detí</p>
        </div>

        <div className="bg-white rounded-3xl border-3 border-[#F0F4F8] p-8 lg:p-12">
          {content?.content ? (
            <RichTextContent html={content.content} />
          ) : (
            <p className="text-[#64748B]">Obsah stránky nebyl nalezen.</p>
          )}
        </div>

        <div className="mt-10" data-testid="parents-documents-section">
          <h2 className="text-2xl font-bold text-[#2D3748] mb-4 flex items-center gap-3">
            <FileText size={26} style={{ color: colors.primary }} />
            Dokumenty pro rodiče
          </h2>
          {documents.length > 0 ? (
            <div className="space-y-3">
              {documents.map((doc, index) => (
                <div
                  key={doc.id}
                  className="document-card animate-fade-in-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`parents-document-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${colors.primary}15` }}>
                    <FileText size={24} style={{ color: colors.primary }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#2D3748] truncate">{doc.title}</h3>
                    {doc.description && (
                      <p className="text-sm text-[#64748B] truncate">{doc.description}</p>
                    )}
                    <div className="text-xs text-[#64748B] mt-1">{formatDate(doc.created_at)}</div>
                  </div>
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium hover:opacity-90 transition-opacity flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                    data-testid={`parents-document-download-${index}`}
                  >
                    <Download size={16} />
                    <span className="hidden sm:inline">Stáhnout</span>
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border-3 border-[#F0F4F8]">
              <FolderOpen size={40} className="mx-auto text-[#CBD5E1] mb-3" />
              <div className="text-[#64748B]">Zatím nejsou k dispozici žádné dokumenty.</div>
            </div>
          )}
        </div>

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <Link 
            to={`/${siteKey}/dokumenty?category=pro_rodice`} 
            className="rounded-2xl p-6 hover:shadow-lg transition-shadow"
            style={{ backgroundColor: `${colors.primary}10` }}
            data-testid="parents-docs-link"
          >
            <h3 className="font-semibold mb-2" style={{ color: colors.primary }}>Všechny dokumenty</h3>
            <p className="text-sm text-[#64748B]">Procházet všechny dokumenty pro rodiče</p>
          </Link>
          <Link 
            to={`/${siteKey}/jidelnicek`} 
            className="bg-[#FFF9C4] rounded-2xl p-6 hover:shadow-lg transition-shadow"
            data-testid="parents-menu-link"
          >
            <h3 className="font-semibold text-[#92400E] mb-2">Jídelnícek</h3>
            <p className="text-sm text-[#64748B]">Aktuální týdenní menu</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
